<template>
	<span ref="elRef" :flex="flex ? flex : null" class="to-progress" :class="setClass" :style="setStyle">
		<template v-if="type === 'circle'">
			<svg class="svg" viewBox="0 0 100 100">
				<circle cx="50" cy="50" :r="radius" fill="none" :stroke-width="thick" class="to-progress-bg" />
				<circle cx="50" cy="50" :r="radius" fill="none" :stroke-width="barThick" transform="rotate(-90, 50, 50)" :stroke-dashoffset="strokeDashoffset" :stroke-dasharray="strokeDasharray" class="to-progress-bar" />
			</svg>
			<div class="to-progress-text">
				<slot :title="titleValue" :content="content">
					<div class="to-progress-text-title">{{ titleValue }}</div>
					<div v-if="content" class="to-progress-text-content">{{ content }}</div>
				</slot>
			</div>
		</template>
		<template v-else>
			<span class="to-progress-bg">
				<span :style="setBarStyle" class="to-progress-bar">
					<span v-if="textPosition === 'inner'" class="to-progress-text">{{ textValue }}</span>
				</span>
			</span>
			<span v-if="textPosition === 'outer' && type !== 'loading'" class="to-progress-text" :style="setTextStyle">
				{{ textValue }}
			</span>
		</template>
	</span>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

export default defineComponent({
	name: 'ToProgress',
	props: {
		flex: Boolean,
		// 类型：normal 或 loading
		type: {
			type: String,
			default: 'normal',
			validator: (val: string) => ['normal', 'loading', 'circle'].includes(val)
		},
		// 进度条百分比，0~100的数字
		value: {
			type: [Number, String],
			default: 0,
			required: true
		},
		// 模式
		mode: {
			type: String,
			default: 'default'
		},
		// 文字
		text: {
			type: String,
			default: ''
		},
		// 标题
		title: {
			type: String,
			default: undefined
		},
		// 内容
		content: {
			type: String,
			default: ''
		},
		// 进度位置：outer(在外)、inner(在内)、none(不显示进度)
		textPosition: {
			type: String,
			default: 'inner',
			validator: (val: string) => ['outer', 'inner', 'none'].includes(val)
		},
		// 显示值的宽度设置
		textWidth: {
			type: [Number, String],
			default: 'auto'
		},
		// 颜色
		color: {
			type: String,
			default: 'primary'
		},
		// 宽度
		width: {
			type: [Number, String],
			default: ''
		},
		// 进度条大小
		barSize: {
			type: [Number, String],
			default: 1
		}
	},
	setup(props) {
		// 响应式状态
		const elRef = ref<HTMLElement | null>(null)
		const radius = ref<number>(0)
		const thick = ref<number>(0)
		const barRadius = ref<number>(0)
		const barThick = ref<number>(0)
		const em = ref(0)
		const offsetWidth = ref(0)
		const iPercentage = ref('')

		const getColor = (color: string) => {
			let c = color
			// 随机色彩逻辑，实际项目中可能需要根据实际环境调整
			let colors = ['primary', 'success', 'warning', 'danger', 'info', 'secondary', 'fg']
			let number = Math.round(Math.random() * 6)
			if (color === 'radam') {
				c = colors[number]
			}

			switch (c) {
				case 'fg':
					return '#000'
				case 'info':
					return '#999'
				case 'warning':
					return '#f3b100'
				case 'success':
					return '#8fc320'
				case 'danger':
					return '#e40077'
				case 'secondary':
					return '#4d5b65'
				case 'primary':
					return '#0085d0'
				default:
					return color
			}
		}

		const circumference = computed(() => 2 * Math.PI * radius.value)
		const strokeDasharray = computed(() => circumference.value)
		const strokeDashoffset = computed(() => circumference.value * (1 - Number(props.value) / 100))

		const textValue = computed(() => {
			if (props.text) {
				return props.text
			} else {
				return iPercentage.value
			}
		})

		const titleValue = computed(() => {
			if (props.title !== undefined) {
				return props.title
			} else {
				// 如果是100%，可以显示完成标识
				const percentValue = Number(iPercentage.value.replace('%', ''))
				if (percentValue >= 100 && props.type === 'circle') {
					return '100%'
				}
				return iPercentage.value
			}
		})

		const setClass = computed(() => {
			const arr: string[] = []
			arr.push(`color-${props.color}`)
			if (props.textPosition) {
				arr.push(`text-position-${props.textPosition}`)
			}
			if (props.type) {
				arr.push(`type-${props.type}`)
			}
			if (props.mode) {
				arr.push(`mode-${props.mode}`)
			}
			return arr
		})

		const setStyle = computed(() => {
			const obj: Record<string, string> = {}
			if (props.width) {
				if (props.type === 'circle') {
					if (!isNaN(Number(props.width))) {
						// 保持1:1的宽高比
						const size = Number(props.width) + 'em'
						obj.width = size
						obj.height = size
					} else if (props.width === 'full') {
						obj.width = '100%'
						// 强制保持1:1的宽高比
						obj.aspectRatio = '1/1'
					} else {
						obj.width = props.width as string
						obj.height = props.width as string
					}
				} else {
					if (!isNaN(Number(props.width))) {
						obj.width = props.width + 'em'
					} else if (props.width === 'full') {
						obj.width = '100%'
					} else {
						obj.width = props.width as string
					}
				}
			}
			return obj
		})

		const setBarStyle = computed(() => {
			return 'width:' + iPercentage.value
		})

		const setTextStyle = computed(() => {
			if (props.textWidth === 'auto') {
				return ''
			} else {
				return 'width:' + props.textWidth + 'em'
			}
		})

		// 方法
		const setPercentage = () => {
			let p = 0
			if ((props.value + '').indexOf('%') > -1) {
				p = Number((props.value as string).replace('%', ''))
			} else {
				p = Number(props.value)
			}
			if (p < 0) {
				p = 0
			}
			iPercentage.value = p + '%'
		}

		const init = () => {
			let timer: number | null = null
			timer = window.setTimeout(() => {
				em.value = 12 // 基础单位，可能需要根据实际项目调整
				const el = document.querySelector('.to-progress') as HTMLElement
				if (el) {
					if (props.type === 'circle') {
						// 获取基础值
						const circleBgTick = parseFloat(getComputedStyle(elRef.value).getPropertyValue('--thick'))
						const circleBarTick = parseFloat(getComputedStyle(elRef.value).getPropertyValue('--bar-thick'))
						const width = parseFloat(getComputedStyle(elRef.value).getPropertyValue('--width'))

						// 计算半径
						thick.value = (circleBgTick / width) * 100
						barThick.value = (circleBarTick / width) * 100
						radius.value = 50 - Math.max(thick.value, barThick.value) / 2

						// 环形进度条使用固定尺寸
						offsetWidth.value = 120 // 默认大小为120px

						// 如果指定了width属性，则覆盖默认值
						if (typeof props.width === 'number') {
							offsetWidth.value = props.width * 16 // 转换em为px
						} else if (props.width === 'full' && el.parentElement) {
							offsetWidth.value = el.parentElement.offsetWidth
						}
					} else {
						offsetWidth.value = el.offsetWidth
					}
				}
				if (timer) {
					clearTimeout(timer)
					timer = null
				}
			}, 200)
		}

		// 生命周期钩子
		onMounted(() => {
			window.addEventListener('resize', init)
			nextTick(() => {
				init()
			})
		})

		onBeforeUnmount(() => {
			window.removeEventListener('resize', init)
		})

		// 监听属性变化
		watch(
			() => props.value,
			() => {
				setPercentage()
			}
		)

		// 初始化
		setPercentage()

		return {
			thick,
			elRef,
			em,
			barThick,
			barRadius,
			offsetWidth,
			iPercentage,
			getColor,
			radius,
			strokeDasharray,
			strokeDashoffset,
			textValue,
			titleValue,
			setClass,
			setStyle,
			setBarStyle,
			setTextStyle
		}
	}
})
</script>
